// messageProtocol.ts
// ★ postMessage wire 포맷의 TS type 정의 (본 spec 08-4).
//   상수 (`INTEGRATION_MESSAGE_TYPE`) 는 constants.ts 가 단일 owner — 본 모듈은 type 만.
//   ⚠ index.html 의 인라인 sender(통합 student-up) 가 같은 string literal 을 직접 소유 — 양쪽 변경 시 동기 필수.
//
// ─── iframe pull 방향 (receiver(통합 student) 가 sender(통합 student-up) 스토리지 끌어옴) ─────────────────────
//   receiver(student.{root})                      sender(student-up iframe)
//          │ ⑴ iframe 생성 (?from=<receiver origin>) ─▶│
//          │                                            │ ⑵ HTML parse → inline script 실행
//          │                                            │   addEventListener('message', ...) 등록 후 대기
//          │ ⑶ iframe.onload → REQUEST_SYNC ──────────▶│   (sender listener 등록 보장 후 송신)
//          │ ◀── ⑷ SYNC_SUCCESS{...} OR SYNC_EMPTY ─── │   storage 동기 read 후 응답
//          │ ⑸ replaceStorage (internal 보존)            │
//          │ ⑹ SYNC_COMPLETED ────────────────────────▶│ marker 저장 (Phase 2+ 에서 추가)
//          │ ⑺ reload                                    │
//          ▼                                            ▼
//
// ─── redirect 방향 (sender(통합 student-up) 가 receiver(통합 student) 로 location.replace) ───────────────────
//   sender(student-up.{root})                     receiver(student iframe, /integration/probe?from=<sender>)
//          │ ⑴ probe iframe 생성 (?from=<sender>) ─▶│
//          │                                         │ ⑵ HTML parse → 인라인 자발 PROBE_RESPONSE
//          │ ◀── ⑶ PROBE_RESPONSE ─────────────────│ (iframe.load 대기 안 함 — Vite dev race 회피)
//          │ ⑷ window.location.replace (redirect)   │
//          ▼                                         ▼

import { INTEGRATION_MESSAGE_TYPE } from './constants'

// IndexedDB 필기 노트 1건 — composite key + NoteData + timestamp.
//   indexedDb.service.ts 의 StudentWorksheetNoteValue 와 동일 shape.
//   여기서 unknown 으로 둠 — receiver(통합 student) 는 그대로 put 만, sender(통합 student-up) 는 그대로 read 만 하므로 type 검증 불요.
export type IndexedDBNoteEntry = {
  studentWorksheetId: number
  worksheetProblemId: number
  noteData: unknown
  timestamp: number
}

// SyncPayload + 개별 메시지 타입은 IntegrationMessage union 내부 구성용 — 외부 import ✗ (internal).
type SyncPayload = {
  localStorage: Record<string, string>
  sessionStorage: Record<string, string>
  // ★ 베이직 student는 필기 미지원 했으므로 → IndexedDB 비어있음. sender(통합 student-up) 우선 덮어쓰기
  //   optional — sender(통합 student-up) 가 이 필드 없을수도 있음
  indexedDBNotes?: IndexedDBNoteEntry[]
}

// REQUEST_SYNC 는 receiver(통합 student) 가 보내는 핸드셰이크 시작.
type RequestSync = {
  type: typeof INTEGRATION_MESSAGE_TYPE.REQUEST_SYNC
}
type SyncSuccess = { type: typeof INTEGRATION_MESSAGE_TYPE.SYNC_SUCCESS } & SyncPayload
type SyncEmpty = { type: typeof INTEGRATION_MESSAGE_TYPE.SYNC_EMPTY }
// SYNC_COMPLETED — receiver(통합 student) 가 storage 교체 완료 후 sender(통합 student-up) 에게 marker 저장 요청.
type SyncCompleted = { type: typeof INTEGRATION_MESSAGE_TYPE.SYNC_COMPLETED }
// PROBE_RESPONSE — receiver(통합 student) 인라인이 ?from= query 보고 자발 발신 (sender(통합 student-up) 의 iframe.load 대기 ✗).
type ProbeResponse = { type: typeof INTEGRATION_MESSAGE_TYPE.PROBE_RESPONSE }
// DEV_RESET_DONE — dev panel 원격 reset 완료 ACK (IntegrationDevReset.page.tsx → dev panel).
type DevResetDone = { type: typeof INTEGRATION_MESSAGE_TYPE.DEV_RESET_DONE; cleared?: string[] }
// LOGOUT_CLEAR — 명시 로그아웃 시 반대 origin 의 storage clear
//   인라인 핸들러가 ?from= query 의 origin 과 event.origin 매칭 후 localStorage/sessionStorage clear.
type LogoutClear = { type: typeof INTEGRATION_MESSAGE_TYPE.LOGOUT_CLEAR }
// LOGOUT_CLEAR_ACK — 인라인이 storage clear 마친 후 parent 에게 알림. parent 는 ack 받으면 즉시 finalize.
type LogoutClearAck = { type: typeof INTEGRATION_MESSAGE_TYPE.LOGOUT_CLEAR_ACK }
export type IntegrationMessage =
  | RequestSync
  | SyncSuccess
  | SyncEmpty
  | SyncCompleted
  | ProbeResponse
  | DevResetDone
  | LogoutClear
  | LogoutClearAck

export const INTEGRATION_IFRAME_MESSAGE = {
  REQUEST_SYNC: {
    type: INTEGRATION_MESSAGE_TYPE.REQUEST_SYNC,
  },
  SYNC_EMPTY: {
    type: INTEGRATION_MESSAGE_TYPE.SYNC_EMPTY,
  },
  SYNC_COMPLETED: {
    type: INTEGRATION_MESSAGE_TYPE.SYNC_COMPLETED,
  },
  PROBE_RESPONSE: {
    type: INTEGRATION_MESSAGE_TYPE.PROBE_RESPONSE,
  },
  LOGOUT_CLEAR: {
    type: INTEGRATION_MESSAGE_TYPE.LOGOUT_CLEAR,
  },
  LOGOUT_CLEAR_ACK: {
    type: INTEGRATION_MESSAGE_TYPE.LOGOUT_CLEAR_ACK,
  },
  SYNC_SUCCESS: (payload: SyncPayload): SyncSuccess => ({
    type: INTEGRATION_MESSAGE_TYPE.SYNC_SUCCESS,
    ...payload,
  }),
  DEV_RESET_DONE: (cleared: string[]): DevResetDone => ({
    type: INTEGRATION_MESSAGE_TYPE.DEV_RESET_DONE,
    cleared,
  }),
} as const
